import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { AlertCircle } from 'lucide-react'
import type { DungeonInput } from '@creanizer/types'

interface DungeonFormBatchProps {
  input: DungeonInput
  onChange: (input: DungeonInput) => void
}

export function DungeonFormBatch({ input, onChange }: DungeonFormBatchProps) {
  return (
    <div className="space-y-4">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          バッチモードでは複数のダンジョンを一度に生成します。
          データサイズが大きいため、最大5個までの生成を推奨します。
        </AlertDescription>
      </Alert>
      
      <div>
        <Label>生成数: {input.numberOfDungeons || 3}</Label>
        <Slider
          value={[input.numberOfDungeons || 3]}
          onValueChange={(value) => onChange({ ...input, numberOfDungeons: value[0] })}
          min={1}
          max={5}
          step={1}
        />
        <p className="text-xs text-muted-foreground mt-1">
          推奨: 3個以下（大量生成は時間がかかり、エラーが発生しやすくなります）
        </p>
      </div>
      
      <div>
        <Label>共通テーマ</Label>
        <Input
          value={input.batchTheme || ''}
          onChange={(e) => onChange({ ...input, batchTheme: e.target.value })}
          placeholder="例：古代文明の遺跡群、魔王軍の拠点、次元の裂け目"
        />
        <p className="text-sm text-muted-foreground mt-1">
          生成されるすべてのダンジョンに共通するテーマを設定します
        </p>
      </div>
      
      <div>
        <Label>ダンジョン間の関連</Label>
        <Select 
          value={input.batchConnection} 
          onValueChange={(value) => onChange({ ...input, batchConnection: value as DungeonInput['batchConnection'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="関連性を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              なし - 独立したダンジョン
            </SelectItem>
            <SelectItem value="linked">
              関連あり - 共通の背景を持つ
            </SelectItem>
            <SelectItem value="sequential">
              連続的 - 順番に攻略する
            </SelectItem>
            <SelectItem value="branching">
              分岐 - 選択式の攻略ルート
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>共通設定（オプション）</Label>
        <Textarea
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="すべてのダンジョンに適用される共通設定&#10;&#10;例：&#10;・同じ文明が建造した施設群&#10;・段階的に難易度が上がる構成&#10;・それぞれ異なる属性（火、水、風、土）&#10;・時代の異なる同一場所"
          rows={6}
          className="resize-none"
        />
      </div>
      
      {input.batchConnection === 'sequential' && (
        <Alert>
          <AlertDescription>
            連続的モード：各ダンジョンは前のダンジョンのクリアを前提として設計されます。
            難易度や規模が段階的に上昇します。
          </AlertDescription>
        </Alert>
      )}
      
      {input.batchConnection === 'branching' && (
        <Alert>
          <AlertDescription>
            分岐モード：プレイヤーの選択によって異なるダンジョンを攻略できるように設計されます。
            それぞれ異なる報酬や体験を提供します。
          </AlertDescription>
        </Alert>
      )}
      
      <div className="p-4 bg-muted rounded-lg">
        <p className="text-sm font-medium mb-2">生成予定：</p>
        <ul className="text-sm space-y-1">
          {Array.from({ length: input.numberOfDungeons || 3 }, (_, i) => (
            <li key={i}>
              • ダンジョン{i + 1}
              {input.batchTheme && ` - ${input.batchTheme}`}
              {input.batchConnection === 'sequential' && ` (レベル${i + 1})`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}